// src/components/duty/LogoutOnDutyModal.tsx — Sign-In System (Build 1).
//
// Shown from MePage's Sign out when the user has an active duty session.
// PHASE_8_ADDENDUM §4: "Stay on duty" keeps the session live across logout —
// the duty row is NOT tied to the auth token, so the sage card reappears on
// next login with the original signedInAt. "Sign off duty & log out" ends the
// shift first (which also clears any active Lone Working).

import { useEffect } from "react";
import { D, DF } from "./dutyTokens";

export function LogoutOnDutyModal({
  open, onCancel, onSignOffAndLogout, onStayOnAndLogout,
}: {
  open: boolean;
  onCancel: () => void;
  onSignOffAndLogout: () => void;
  onStayOnAndLogout: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="You are still on duty. Sign off duty as well?"
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(43,32,20,0.44)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 240 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 380, background: D.card, borderRadius: 22, padding: "22px 20px 18px", border: `1.5px solid ${D.duty}`, boxShadow: "0 16px 48px rgba(0,0,0,0.32)" }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: D.dutyBg, border: `1.5px solid ${D.duty}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <SignOffIcon size={28} color={D.dutyDk} />
        </div>
        <div style={{ textAlign: "center", fontFamily: DF.body, fontSize: 10.5, fontWeight: 800, letterSpacing: "1.6px", textTransform: "uppercase", color: D.dutyDk, marginBottom: 6 }}>You're still on duty</div>
        <div style={{ textAlign: "center", fontFamily: DF.serif, fontSize: 19, fontWeight: 700, fontStyle: "italic", color: D.ink, marginBottom: 8 }}>Sign off duty as well?</div>
        <div style={{ fontFamily: DF.serif, fontStyle: "italic", fontSize: 13.5, color: D.inkSoft, textAlign: "center", lineHeight: 1.5, marginBottom: 18 }}>
          Stay on and the clock keeps running. You'll still appear in the timesheet, and group messages will reach you next time you sign in.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button onClick={onSignOffAndLogout} style={{ minHeight: 48, padding: "12px 16px", background: D.duty, color: "#fff", border: "none", borderRadius: 12, fontFamily: DF.body, fontSize: 14.5, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 6px rgba(79,104,82,0.22)" }}>
            <SignOffIcon size={14} color="#fff" /> Sign off duty &amp; log out
          </button>
          <button onClick={onStayOnAndLogout} style={{ minHeight: 48, padding: "12px 16px", background: D.dutyBg, color: D.dutyDk, border: `1.5px solid ${D.duty}`, borderRadius: 12, fontFamily: DF.body, fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>
            Stay on duty · just log out
          </button>
        </div>
      </div>
    </div>
  );
}

function SignOffIcon({ size, color = "currentColor" }: { size: number; color?: string }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></svg>);
}
